import axios from 'axios';
import ConfigUtils from "qwc2/utils/ConfigUtils";
import {LayerRole} from "qwc2/actions/layers";
import clientAuth from "../ClientAuth";

const MapStateService = {
    getUrl() {
        return ConfigUtils.getConfigProp("mapstateServiceUrl");
    },

    getLayerOptions(layers, result) {
        layers.forEach(l => {
            if (l.role === LayerRole.USERLAYER) {
                result[l.id] = {
                    layerid: l.id
                };
                if (typeof l.opacity !== 'undefined') {
                    result[l.id].opacity = l.opacity;
                }
                if (typeof l.visibility !== 'undefined') {
                    result[l.id].visibility = l.visibility;
                }
            }
            if (l.sublayers && l.sublayers.length) {
                MapStateService.getLayerOptions(l.sublayers, result);
            }
        });
        return result;
    },

    buildState(map, layers) {
        const layerOptions = MapStateService.getLayerOptions(layers, {});
        return {
            zoom: map.zoom,
            lat: map.center[0],
            lon: map.center[1],
            layers: Object.keys(layerOptions).map(lid => layerOptions[lid])
        };
    },

    // Resolves with the list of mapstates, an empty list if there is none (api sends a 404).
    // Any other error is passed on, so that callers do not mistake a failure for an empty list.
    list() {
        return axios.get(MapStateService.getUrl(), clientAuth.headers())
            .then(res => (res && res.data && res.data.mapstates) || [])
            .catch(error => {
                if (error.response && error.response.status === 404) {
                    return [];
                }
                throw error;
            });
    },

    create(data) {
        return axios.post(MapStateService.getUrl(), data, clientAuth.headers());
    },

    update(id, data) {
        return axios.put(MapStateService.getUrl() + id, data, clientAuth.headers());
    },

    delete(id) {
        return axios.delete(MapStateService.getUrl() + id, clientAuth.headers());
    },

    setDefault(id, value) {
        return MapStateService.update(id, {isDefault: value});
    },

    isDefault(mapstate) {
        return mapstate.isDefault === 1 || mapstate.isDefault === "1" || mapstate.isDefault === true;
    },

    /**
     * Removes duplicates of the mapstate with the given name from the passed list of mapstates.
     * The default mapstate is kept, the non-default duplicates are deleted. If none of them is the default
     * (or several are), the one with the smallest id is kept, so that concurrent callers decide the same way.
     * Resolves with the kept mapstate, or null if there is no mapstate with that name.
     */
    removeDuplicates(list, name) {
        const matches = list.filter(ms => ms.name === name);
        if (matches.length === 0) {
            return Promise.resolve(null);
        }
        const sorted = [...matches].sort((a, b) => {
            return (MapStateService.isDefault(b) ? 1 : 0) - (MapStateService.isDefault(a) ? 1 : 0)
                || String(a.id).localeCompare(String(b.id));
        });
        const keep = sorted[0];
        return Promise.all(sorted.slice(1).map(ms => {
            return MapStateService.delete(ms.id).catch(error => {
                // already deleted by a concurrent caller, or temporarily not possible, the next run will retry
                console.log('Could not delete duplicate mapstate ' + name, error);
            });
        })).then(() => keep);
    }
};

export default MapStateService;
